import { UserEventV2 } from "@backend-types";
import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useEffect, useRef } from "react";
import { uniqueArray } from "../../Helpers";
import { MacsAuthenticator } from "../../hooks/useMacsAuthenticator";
import {
  DbUser,
  Lti1p0LaunchParams,
  PostMessage,
  UserCtx,
  UserSubmission,
} from "../../interfaces";
import { EventPayload, validateEventData } from "../../json/JSONValidation";
import { ErrorPage } from "../../pages/ErrorPage";
import { Question, Target } from "../utils/CourseTree";
import { Lti1p3LaunchError } from "./Lti1p3Lxp";

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    overflow: "hidden",
    position: "relative",
  },
  iframe: {
    width: "100%",
    height: "100%",
    border: "none",
  },
}));

interface Lti1p0SolverProps {
  iframeRef: React.RefObject<HTMLIFrameElement>;
  launchParams: Lti1p0LaunchParams;
  launchError: Lti1p3LaunchError | null;
  currentQuestion: Question;
  currentUser: DbUser;
  userCtx: UserCtx;
  userCtxRef: React.MutableRefObject<UserCtx>;
  showFeedback: (status: "success" | "error") => void;
  handleTriggerRemediation: (skillId: string, triggered?: boolean) => void;
  handleIncrementInteraction: () => void;
}

export const Lti1p0Solver = (props: Lti1p0SolverProps) => {
  const {
    iframeRef,
    launchParams,
    launchError,
    currentQuestion,
    userCtxRef,
    currentUser,
    showFeedback,
    handleTriggerRemediation,
    handleIncrementInteraction,
  } = props;
  const classes = useStyles();
  const lti1p0FormRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    const validateLaunchParams = (params: Lti1p0LaunchParams | null): boolean => {
      if (!params) return false;
      if (!params.launchValues.ltiLaunchUrl) return false;
      return true;
    };

    if (validateLaunchParams(launchParams)) {
      lti1p0FormRef?.current?.submit();
    }
  }, [launchParams]);

  useEffect(() => {
    const handleMessage = async (event: MessageEvent<EventPayload>) => {
      const payload = event.data as EventPayload;
      console.log(payload);
      const questionId = currentQuestion.id;
      const classId =
        MacsAuthenticator.authHook.studentCurrentClassId === null
          ? null
          : Number(MacsAuthenticator.authHook.studentCurrentClassId);
      // get the current attempt number
      const submissions = userCtxRef.current.userSubmissions
        .filter((item) => item.question_id === questionId)
        .sort(
          (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
      const currentAttempt = submissions.length + 1;
      const mostRecentSubmission = submissions.length > 0 ? submissions[0] : null;

      const saveSubmission = async (
        questionId: string,
        points: number,
        maxPoints: number,
        v2: boolean,
        restoredSubmission?: boolean
      ) => {
        const newSubmission = await userCtxRef.current.insertUserSubmission(
          questionId,
          points,
          maxPoints,
          restoredSubmission,
          undefined,
          v2 // tells backend not to create a v1 user event if the points !== maxPoints
        );

        // show feedback message on bottom bar
        if (!restoredSubmission) {
          if (points !== maxPoints) {
            showFeedback("error");
            // if the last submission and most recent submission are incorrect it should trigger the user
            // remediation for the target, a full review, per Kelynn

            const bothIncorrect =
              mostRecentSubmission &&
              newSubmission &&
              mostRecentSubmission.points !== mostRecentSubmission.max_points &&
              newSubmission.points !== maxPoints;
            if (bothIncorrect) {
              const currentTarget = currentQuestion.parent as Target;
              if (!v2) {
                // generate a user event for the incorrect submission
                let postMessages: PostMessage[] = [];
                postMessages.push({
                  question_id: questionId,
                  type: "viewFeedback",
                  id: `${newSubmission?.id ?? "generated"}`,
                  target: "other",
                  status: "error",
                  lo: "other",
                  levels: 0,
                  currentLevel: 0,
                  attempts: 0,
                  skillId: currentTarget.id,
                });
                userCtxRef.current.insertUserEvents(postMessages);
              } else {
                // need the v2 equivalent
              }
              handleTriggerRemediation("other", true);
            }
          } else {
            showFeedback("success");
          }
        }
        //

        if (newSubmission) {
          // immediately push the submission into the userCtx state to allow updating frontend
          const newUserSubmissions =
            uniqueArray(userCtxRef.current.userSubmissions) ?? [];
          const newQuestionSubmitted =
            uniqueArray(userCtxRef.current.questionsSubmitted) ?? [];

          newUserSubmissions.push(newSubmission);
          userCtxRef.current.setUserSubmissions([...newUserSubmissions]);
          userCtxRef.current.setQuestionsSubmitted([...newQuestionSubmitted, questionId]);
          await userCtxRef.current.deleteUserTryAgain(questionId);
        }
        return newSubmission;
      };
      try {
        if (!payload || typeof payload !== "object") {
          throw new Error("Invalid payload received");
        }
        if (isEventPayload(payload)) {
          let payloadMetadata: Object = {};
          let newSubmission: UserSubmission | undefined;

          if (payload.type === "attempt_submitted") {
            const { points = 0, maxPoints = 10 } = payload;

            payloadMetadata = { points, maxPoints };
            // send the new submission to backend DB
            newSubmission = await saveSubmission(questionId, points, maxPoints, false);
          }

          const transformedPayload = {
            // Tool provided
            type: payload.type,
            status: payload.status,
            contents: payload.contents,
            current_hint_level: payload.current_hint_level,
            total_hint_levels: payload.total_hint_levels,
            learning_objectives: payload.learning_objectives,
            skill_ids: payload.skill_ids,
            created_at: payload.created_at,
            // Digit controlled
            question_id: questionId,
            class_id: classId,
            submission_id: newSubmission?.id,
            attempt: currentAttempt,
            metadata: {
              ...payloadMetadata,
            },
          } as Omit<UserEventV2, "id" | "user_id">;

          userCtxRef.current.insertUserEventsV2(transformedPayload);
        } else {
          // legacy gradarius payload handling
          // We can decomission this else statement once Gradarius is migrated to the v2 structure

          const postMessage = event.data as any;
          console.log(postMessage);
          const { type } = postMessage;

          if (type === "submitSolution" || type === "turnIn") {
            console.log("submitSolution Postmessage", postMessage);
            const { points, maxPoints = 10 } = postMessage.payload;
            // const targetSkills = selectedTarget?.getAllTargetSkills();
            const fallbackPoints = Number(points ?? postMessage.score ?? 0);
            await saveSubmission(questionId, fallbackPoints, maxPoints, false);
          } else if (
            type === "viewFeedback" ||
            type === "viewGeneralHints" ||
            type === "reviewSolution"
          ) {
            /**Submission regeneration for recovering from failed try agains or submissions */
            if (type === "reviewSolution" && postMessage.payload.score) {
              if (
                currentQuestion?.is_question_submitted === false &&
                currentQuestion?.is_question_retried === false
              ) {
                const { points, maxPoints } = postMessage.payload.score;
                saveSubmission(questionId, points, maxPoints, false, true);
              } else if (
                currentQuestion?.is_question_submitted &&
                currentQuestion?.is_question_retried
              ) {
                console.warn(
                  "Digit desynced with Gradarius, attempting try-again state repair."
                );
                await userCtxRef.current.deleteUserTryAgain(questionId);
              }
            }
            /** */
            handleIncrementInteraction();

            let payload: PostMessage[] = [];
            let postMessages: PostMessage[] = [];
            if (postMessage.payload.feedback) {
              if (Array.isArray(postMessage.payload.feedback)) {
                payload.push(...postMessage.payload.feedback);
              } else {
                payload.push(postMessage.payload.feedback);
              }
            } else {
              if (Array.isArray(postMessage.payload)) {
                payload.push(...postMessage.payload);
              } else {
                payload.push(postMessage.payload);
              }
            }
            payload.forEach((postMessagePayload: PostMessage) => {
              const postMessage = {} as PostMessage;
              postMessage.question_id = questionId;
              postMessage.type = type;
              postMessage.id = postMessagePayload.id;
              postMessage.target = postMessagePayload.target;
              postMessage.status = postMessagePayload.status;
              postMessage.lo = postMessagePayload.lo;
              postMessage.levels = postMessagePayload.levels;
              postMessage.currentLevel = postMessagePayload.currentLevel;
              postMessage.attempts = currentAttempt;
              postMessage.skillId = postMessagePayload.skillId;
              postMessages.push(postMessage);

              // if levels are numbers & matched, trigger remediation
              const triggerRemediation =
                postMessage.levels &&
                postMessage.currentLevel &&
                postMessage.levels === postMessage.currentLevel
                  ? true
                  : false;
              handleTriggerRemediation(
                postMessage.skillId ?? "other",
                triggerRemediation
              );

              // let skillIdArray = customizeSkillIdMap.get(questionId);
              // if (!skillIdArray) {
              //   skillIdArray = [];
              // }
              // if (skillIdArray.indexOf(skillId) === -1) {
              //   skillIdArray.push(skillId);
              //   customizeSkillIdMap.set(questionId, skillIdArray);
              // }
              // setRemediationTriggered(true);
              // handleRemediationTriggered(true);
              // if v2, trigger remediation check immediately rather than during submission
              // if (userCtx.checkFeatureFlagAccess(2)) {
              //   handleRemediationTriggered(true);
              // }
            });
            userCtxRef.current.insertUserEvents(postMessages);
          }
        }
      } catch (e) {
        console.log(e);
      }
    };

    window.addEventListener("message", handleMessage);

    // Cleanup event listener on component unmount
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [
    currentQuestion.id,
    currentQuestion.is_question_retried,
    currentQuestion.is_question_submitted,
    currentQuestion.parent,
    currentUser,
    userCtxRef,
    showFeedback,
    handleTriggerRemediation,
    handleIncrementInteraction,
  ]);

  return (
    <Box className={classes.root}>
      {launchError ? (
        <ErrorPage title={launchError.title} description={launchError.description} />
      ) : (
        <>
          {launchParams && (
            <>
              <form
                ref={lti1p0FormRef}
                method={launchParams.launchValues.formMethod}
                action={launchParams.launchValues.ltiLaunchUrl}
                target="lti1p0iframe">
                {launchParams.formFields &&
                  Object.entries(launchParams.formFields).map(([key, value]) => {
                    return <input type="hidden" name={key} value={value} key={key} />;
                  })}
              </form>
              <iframe
                allowFullScreen={true}
                title="LTI 1p0 Frame"
                ref={iframeRef}
                className={classes.iframe}
                name="lti1p0iframe"></iframe>
            </>
          )}
          {launchParams === null && (
            <Box className={classes.iframe}>
              <Stack
                justifyContent="center"
                alignItems="center"
                direction="column"
                sx={{
                  width: "100%",
                  height: "100%",
                }}>
                <Stack justifyContent="center" alignItems="center" direction="column">
                  <CircularProgress color="primary" size={50} />
                  <Typography
                    component="h1"
                    variant="body1"
                    fontSize="2em"
                    fontWeight={700}
                    sx={{ marginTop: "21.44px", marginBottom: "21.44px" }}>
                    Launching your activity
                  </Typography>
                </Stack>
              </Stack>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

const isEventPayload = (item: any): item is EventPayload => {
  try {
    return validateEventData(item) ?? false;
  } catch (e) {
    return false;
  }
};
